// Single row in the sidebar agent list.
//
// Layout (D54, extended by sprint agent-stages):
//   [stage-bar] [status-icon]  Name                 timestamp
//                               subtitle (conditional)  hover: action buttons
//
// Subtitle visibility is state-driven AND recency-driven:
//   - Active states (running/waiting/error/stuck/interrupted) always show.
//   - finished: diff stat only when lastActivityAt is <24 h old. Fence
//     note: D63 scoped the diff stat to the Pinned + Today sections; the
//     stage redesign (spec agent-stages D3) retired those sections, so
//     the ORIGINAL intent — hide stale diff noise on old rows — is kept
//     via the same 24 h boundary the Today bucket used.
//
// Hover affordances (D40, D62, UI-8.2 Cursor-mirror; pin retired per
// spec agent-stages D3 — stages replace pinning, `pinned` survives at
// the data layer only):
//   - Active rows: Archive + Kebab on the right; left status-icon fixed.
//   - Archived rows: Restore / Delete on the right.
//
// Interactions:
//   - Click → select + mark read.
//   - Right-click / long-press → context menu.
//   - Double-click on name (desktop) → inline rename.

import { useEffect, useRef, useState } from 'react';
import type { Agent } from '../types';
import { StatusIcon } from './StatusIcon';
import { Icon } from './atoms/Icon';
import { Badge } from './atoms/Badge';
import { useLongPress } from '../hooks/useLongPress';
import { useMediaQuery, MD_QUERY } from '../hooks/useMediaQuery';
import { useTranslation } from '../i18n';
import type { TKey } from '../i18n';
import { formatRelativeTime } from '../lib/time';

export interface AgentListItemProps {
  agent: Agent;
  selected: boolean;
  renaming: boolean;
  archived: boolean;
  // Sprint agent-stages — hex of the agent's stage color (resolved by the
  // Sidebar via paletteColor). null/undefined = no bar ("Sin estado").
  stageColorHex?: string | null;
  onSelect: () => void;
  onContextMenu: (point: { x: number; y: number }) => void;
  onRenameSubmit: (name: string) => void;
  onRenameCancel: () => void;
  onRenameStart?: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  onDelete?: () => void;
}

const DIFF_STAT_RECENCY_MS = 24 * 60 * 60 * 1000;

// Sprint 12 (D-008) — binary Working / Idle. Only running and error
// show subtitles; finished shows git diff stats on recent rows.
function getSubtitle(
  agent: Agent,
  archived: boolean,
  t: (key: TKey, params?: Record<string, string | number>) => string,
): string | null {
  const { status } = agent;

  if (status === 'running') {
    return agent.lastAgentSummary ?? t('agentList.working');
  }

  if (status === 'error') {
    return agent.errorMessage ?? t('status.error');
  }

  if (status === 'finished') {
    const ts = Date.parse(agent.lastActivityAt);
    const isRecent =
      Number.isFinite(ts) && Date.now() - ts < DIFF_STAT_RECENCY_MS;
    if (!archived && isRecent) {
      const git = agent.gitStatus;
      if (git && git.filesChanged > 0) {
        if (git.linesRemoved === 0) {
          return t('agentList.diffStatNoRemoved', {
            added: git.linesAdded,
            files: git.filesChanged,
          });
        }
        return t('agentList.diffStat', {
          added: git.linesAdded,
          removed: git.linesRemoved,
          files: git.filesChanged,
        });
      }
      return t('agentList.done');
    }
    return null;
  }

  return null;
}

export function AgentListItem({
  agent,
  selected,
  renaming,
  archived,
  stageColorHex,
  onSelect,
  onContextMenu,
  onRenameSubmit,
  onRenameCancel,
  onRenameStart,
  onArchive,
  onRestore,
  onDelete,
}: AgentListItemProps) {
  const { t } = useTranslation();
  const longPress = useLongPress(onContextMenu, { delayMs: 500 });
  const isDesktop = useMediaQuery(MD_QUERY);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onContextMenu({ x: e.clientX, y: e.clientY });
  };

  const nameDoubleClick =
    isDesktop && onRenameStart
      ? (e: React.MouseEvent) => {
          e.stopPropagation();
          onRenameStart();
        }
      : undefined;

  const subtitle = getSubtitle(agent, archived, t);
  const hasSubtitle = subtitle !== null && !renaming;
  const isArchived = archived;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (!renaming) onSelect();
      }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !renaming) {
          e.preventDefault();
          onSelect();
        }
      }}
      onContextMenu={handleContextMenu}
      onTouchStart={longPress.onTouchStart}
      onTouchMove={longPress.onTouchMove}
      onTouchEnd={longPress.onTouchEnd}
      onTouchCancel={longPress.onTouchCancel}
      data-testid={`agent-row-${agent.id}`}
      data-selected={selected ? 'true' : 'false'}
      data-pending={agent.pending ? 'true' : 'false'}
      className={[
        // Sprint `sidebar-row-alignment-and-color` (2026-05-12) —
        // `items-start` so icon + timestamp anchor to the top row (the
        // name line) when the row has a subtitle, instead of floating
        // between the two lines. Single-line rows look identical.
        'group flex items-start gap-gap-tight px-pad-chip-x cursor-pointer select-none',
        'transition-colors duration-motion-instant ease-cursor-out',
        hasSubtitle ? 'py-1.5' : 'py-2',
        selected
          // TODO design-discipline: extract #7C7C7C to a semantic token
          // (proposed name: `agent-row-text` / `text-agent-idle`). Raw
          // value approved by Santi 2026-05-12 chat — wants the row
          // name and icon at #7C7C7C uniformly, darker than the
          // current `text-foreground/74` (~#AAA) which felt too bright.
          // Selected rows keep `text-foreground` (full white) so the
          // selected state still stands out.
          ? 'bg-secondary text-foreground'
          : 'hover:bg-secondary/40 text-[#7C7C7C]',
        // Sprint 11b.3 (D-009 §3.5) — optimistic row treatment: 60%
        // opacity while `POST /api/agents/empty` is in flight. The
        // status icon's pulse comes from `data-pending=true` below.
        agent.pending ? 'opacity-60' : '',
      ].join(' ')}
    >
      {/* Sprint agent-stages — stage color bar. `w-0.5` from the standard
          Tailwind scale; hex comes from the palette map that backs the
          `avatar.palette-NN` tokens (same sanctioned pattern as the
          workspace avatars — see lib/avatar-defaults.ts header). The
          transparent placeholder keeps row alignment identical between
          staged and unstaged rows. */}
      <span
        className="w-0.5 self-stretch rounded-full shrink-0"
        style={stageColorHex ? { backgroundColor: stageColorHex } : undefined}
        aria-hidden="true"
        data-testid={stageColorHex ? `agent-stage-bar-${agent.id}` : undefined}
      />

      {/* Left status slot (Cursor-mirror). Fixed at btn-icon (22×22).
          Sprint agent-stages — the hover Pin swap is retired with the
          pin UI (spec D3); the status icon is now persistent. */}
      <span
        className={[
          'inline-flex h-btn-icon w-btn-icon items-center justify-center shrink-0',
          agent.pending ? 'animate-pulse' : '',
        ].join(' ')}
      >
        <StatusIcon status={agent.status} archived={isArchived} />
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-gap-tight">
          {renaming ? (
            <RenameInput
              initial={agent.name}
              onSubmit={onRenameSubmit}
              onCancel={onRenameCancel}
            />
          ) : (
            <span
              className={[
                'truncate text-body',
                selected ? 'font-550 text-foreground' : 'font-450',
              ].join(' ')}
              data-testid={`agent-name-${agent.id}`}
              onDoubleClick={nameDoubleClick}
            >
              {agent.name}
            </span>
          )}
        </div>
        {hasSubtitle && (
          <p className="text-caption text-muted-foreground truncate mt-0.5">
            {subtitle}
          </p>
        )}
      </div>

      {/* Trailing: timestamp (default) vs action buttons (hover) */}
      {!renaming && (
        <div className="shrink-0 flex items-center">
          {/* Timestamp — hidden on hover (desktop) */}
          <span className="text-caption text-muted-foreground/70 group-hover:hidden">
            {formatRelativeTime(agent.lastActivityAt)}
          </span>

          {/* Hover actions — visible on hover only. Sprint UI-8.1 hits
              pixel parity with `docs/ui/mockups/01-dashboard.html`
              (`.agent-action`, L376-389): 22×22 hit area, 13 px icon,
              8 px gap between buttons, --fg-tertiary idle, MoreVertical
              for the third action. */}
          <div className="hidden group-hover:flex items-center gap-2">
            {isArchived ? (
              <ArchivedHoverButtons
                onRestore={onRestore}
                onDelete={onDelete}
              />
            ) : (
              <ActiveHoverButtons
                onArchive={onArchive}
                onContextMenu={(e) => {
                  e.stopPropagation();
                  onContextMenu({ x: e.clientX, y: e.clientY });
                }}
              />
            )}
          </div>

          {agent.unread && (
            <Badge
              variant="dot"
              color="info"
              className="ml-1.5 shrink-0 group-hover:hidden"
              aria-label={t('agentList.unread')}
              data-testid={`agent-unread-${agent.id}`}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ActiveHoverButtons({
  onArchive,
  onContextMenu,
}: {
  onArchive?: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {onArchive && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onArchive(); }}
          className="h-btn-icon w-btn-icon min-h-btn-icon min-w-btn-icon inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-popover transition-colors duration-motion-instant"
          title={t('tooltip.archive')}
          aria-label={t('tooltip.archive')}
        >
          <Icon name="Archive" size={13} />
        </button>
      )}
      <button
        type="button"
        onClick={onContextMenu}
        className="h-btn-icon w-btn-icon min-h-btn-icon min-w-btn-icon inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-popover transition-colors duration-motion-instant"
        title={t('tooltip.moreActions')}
        aria-label={t('tooltip.moreActions')}
      >
        <Icon name="MoreVertical" size={13} />
      </button>
    </>
  );
}

function ArchivedHoverButtons({
  onRestore,
  onDelete,
}: {
  onRestore?: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {onRestore && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRestore(); }}
          className="h-btn-icon w-btn-icon min-h-btn-icon min-w-btn-icon inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-popover transition-colors duration-motion-instant"
          title={t('tooltip.restore')}
          aria-label={t('tooltip.restore')}
        >
          <Icon name="RotateCw" size={13} />
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="h-btn-icon w-btn-icon min-h-btn-icon min-w-btn-icon inline-flex items-center justify-center rounded text-muted-foreground hover:text-status-error hover:bg-popover transition-colors duration-motion-instant"
          title={t('tooltip.deletePermanently')}
          aria-label={t('tooltip.deletePermanently')}
        >
          <Icon name="Trash2" size={13} />
        </button>
      )}
    </>
  );
}

function RenameInput({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: string;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  return (
    <input
      ref={ref}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          const trimmed = value.trim();
          if (trimmed) onSubmit(trimmed);
          else onCancel();
        } else if (e.key === 'Escape') {
          onCancel();
        }
      }}
      onBlur={() => {
        const trimmed = value.trim();
        if (trimmed && trimmed !== initial) onSubmit(trimmed);
        else onCancel();
      }}
      className="flex-1 min-w-0 h-8 px-2 text-body bg-popover border border-border rounded text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
      data-testid="agent-rename-input"
      aria-label={t('agentList.renameAgent')}
    />
  );
}

